import React, { useEffect, RefObject } from 'react';
import { cn } from '@/lib/core/utils';
import { AIMessageList } from '../AIMessageList';
import type { AIMessageListProps, AIMessage as AIMessageType, AIQuestionAnswers } from '../types';

interface MobileMessagesContainerProps extends Omit<AIMessageListProps, 'messagesEndRef'> {
  containerRef: RefObject<HTMLDivElement>;
  messagesEndRef: RefObject<HTMLDivElement>;
  autoScrollEnabled: boolean;
  onScroll: () => void;
  businessLogoUrl?: string | null;
  businessId?: string | null;
  onQuestionsSubmit?: (answers: AIQuestionAnswers, displayText: string) => void;
  streamingContent?: string;
  onResend?: (message: AIMessageType) => void;
  onEdit?: (message: AIMessageType, newContent: string) => void;
  conversationId?: string | null;
  className?: string;
}

/**
 * Scroll-aware message wrapper for mobile AI chat.
 * Integrates with useMobileChatScroll and handles keyboard-aware scroll adjustment.
 */
export const MobileMessagesContainer: React.FC<MobileMessagesContainerProps> = ({
  containerRef,
  messagesEndRef,
  autoScrollEnabled,
  onScroll,
  messages,
  isLoading,
  onRevert,
  onConfirmTools,
  onCancelTools,
  onSuggestionClick,
  businessLogoUrl,
  businessId,
  onQuestionsSubmit,
  streamingContent,
  onResend,
  onEdit,
  conversationId,
  className,
}) => {
  // Auto-scroll to bottom when streaming content changes
  useEffect(() => {
    if (autoScrollEnabled && (streamingContent || isLoading)) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [streamingContent, isLoading, autoScrollEnabled, messagesEndRef]);

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className={cn(
        'flex-1 overflow-y-auto min-h-0 p-4',
        // Smooth scroll behavior
        'scroll-smooth',
        // Hide scrollbar for cleaner look
        'scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent',
        className
      )}
    >
      <AIMessageList
        messages={messages}
        isLoading={isLoading}
        onRevert={onRevert}
        onConfirmTools={onConfirmTools}
        onCancelTools={onCancelTools}
        onSuggestionClick={onSuggestionClick}
        businessLogoUrl={businessLogoUrl}
        businessId={businessId}
        onQuestionsSubmit={onQuestionsSubmit}
        streamingContent={streamingContent}
        onResend={onResend}
        onEdit={onEdit}
        conversationId={conversationId}
      />
      {/* Invisible element for scroll targeting */}
      <div ref={messagesEndRef} />
    </div>
  );
};
